/**
 * A day sheet's links to the SHARED pressure-control records: the pressure
 * tests (a `rig_up` test among them) and crew drills done during the sheet
 * (PUT /sheets/:id/pressure-tests | /drills, GET …/pressure-control-candidates,
 * fieldiq-api#1338).
 *
 * The records belong to /pressure-control and are read through its readers
 * (readTest / readDrill), never re-shaped here. A record recorded from the
 * field is PENDING until the office approves the sheet, which approves it
 * with the sheet. Links change only while the sheet is a draft; the API's
 * candidate list holds records inside the sheet window, and one already on
 * another sheet cannot be ticked. Unticking a pending record deletes it.
 *
 * The rig-up rule reads these links: a passing rig-up test on the unit's day
 * is what quiets the rig_up_test_missing warning (or the 409, when the
 * setting requires it).
 */
import React, { useMemo, useState } from 'react';
import { Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useSetWlSheetLinks, useWlLinkCandidates } from '../../hooks/useWlJobs';
import { formatInstant } from '../../lib/calendar-date';
import { fmtQty } from '../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { pcDrillKindLabel, pcReviewSkin, pcTestKindLabel, PC_RESULT_LABEL, readDrill, readTest } from '../../lib/pressure-control';
import { wlJobRefusal } from '../../lib/wireline-slickline-jobs';
import type { PcDrill, PcTest } from '../../types/pressure-control';
import type { WlSheet } from '../../types/wireline-slickline-jobs';
import { WL_PRIMARY, WL_SECONDARY, WlEmpty, WlFormError, WlLoading, WlPanel } from './WlBits';

export const wlTestLine = (t: PcTest, tz: string | null) =>
  `${pcTestKindLabel(t.testKind)} · ${formatInstant(t.testedAt, tz) ?? '—'} · ${t.stackName || '—'} · ${t.result ? PC_RESULT_LABEL[t.result] ?? t.result : '—'}`;
export const wlDrillLine = (d: PcDrill, tz: string | null) =>
  `${pcDrillKindLabel(d.drillKind)} · ${formatInstant(d.drilledAt, tz) ?? '—'} · ${
    d.secondsToSecure === null ? '—' : `${fmtQty(d.secondsToSecure, 2)} s to secure`
  }`;

export function WlReviewBadge({ status, testId }: { status: string | null; testId?: string }) {
  const skin = pcReviewSkin(status);
  return (
    <span
      data-testid={testId}
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.64rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

export const sheetTests = (sheet: Pick<WlSheet, 'pressureTests'>) =>
  (sheet.pressureTests ?? []).map((r) => readTest(r as any)).filter((t): t is PcTest => !!t);
export const sheetDrills = (sheet: Pick<WlSheet, 'drills'>) =>
  (sheet.drills ?? []).map((r) => readDrill(r as any)).filter((d): d is PcDrill => !!d);

export function SheetLinksPanel({ sheet, editable }: { sheet: WlSheet; editable: boolean }) {
  const tz = sheet.timeZone ?? null;
  const tests = sheetTests(sheet);
  const drills = sheetDrills(sheet);
  const [managing, setManaging] = useState(false);
  const candidates = useWlLinkCandidates(sheet.id, editable && managing);
  const setLinks = useSetWlSheetLinks();
  const [pickedTests, setPickedTests] = useState<Set<string>>(new Set());
  const [pickedDrills, setPickedDrills] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const failure = queryLoadFailure(candidates);
  const cTests = candidates.data?.data?.pressureTests ?? [];
  const cDrills = candidates.data?.data?.drills ?? [];
  const currentTests = useMemo(() => new Set(tests.map((t) => t.id)), [tests]);
  const currentDrills = useMemo(() => new Set(drills.map((d) => d.id)), [drills]);
  const pendingCount = tests.filter((t) => t.reviewStatus === 'pending').length + drills.filter((d) => d.reviewStatus === 'pending').length;
  const same = (a: Set<string>, b: Set<string>) => a.size === b.size && Array.from(a).every((x) => b.has(x));
  const toggle = (set: Set<string>, id: string, apply: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    apply(next);
  };

  const save = async () => {
    setError(null);
    try {
      if (!same(pickedTests, currentTests)) await setLinks.mutateAsync({ id: sheet.id, kind: 'test', ids: Array.from(pickedTests) });
      if (!same(pickedDrills, currentDrills)) await setLinks.mutateAsync({ id: sheet.id, kind: 'drill', ids: Array.from(pickedDrills) });
      toast.success('Links saved');
      setManaging(false);
    } catch (err) {
      setError(wlJobRefusal(err, 'The links could not be saved.'));
    }
  };

  const row = (c: { id: string; linkedSheetId: string | null; onThisSheet: boolean }, label: string, picked: Set<string>, apply: (s: Set<string>) => void) => {
    const selectable = !c.linkedSheetId || c.onThisSheet;
    return (
      <li key={c.id} className="flex items-start gap-2 text-sm text-fieldiq-text">
        <input type="checkbox" className="mt-1" aria-label={label} checked={picked.has(c.id)} disabled={!selectable} onChange={() => toggle(picked, c.id, apply)} />
        <span className={selectable ? '' : 'text-fieldiq-muted'}>
          {label}
          {!selectable && ' · on another day sheet'}
        </span>
      </li>
    );
  };

  return (
    <WlPanel
      eyebrow="Pressure control"
      title="Pressure tests and drills"
      testId="wl-sheet-links"
      actions={
        editable && !managing ? (
          <button
            type="button"
            className={WL_PRIMARY}
            onClick={() => {
              setError(null);
              setPickedTests(new Set(currentTests));
              setPickedDrills(new Set(currentDrills));
              setManaging(true);
            }}
          >
            <Link2 className="size-4" aria-hidden="true" />
            Link records
          </button>
        ) : undefined
      }
    >
      <div className="space-y-3 px-4 py-4">
        <WlFormError message={error} />
        {managing ? (
          candidates.isLoading ? (
            <WlLoading label="Loading the records done during this sheet" />
          ) : failure ? (
            <LoadFailureNotice error={queryFailureError(candidates)} kind={failure} subject="the pressure-control records" />
          ) : (
            <>
              <p className="text-sm text-fieldiq-muted">
                Records done during this sheet. A record can be on one day sheet.
                {pendingCount > 0 && ' Unticking a pending record deletes it: it was recorded on this sheet and never counted.'}
              </p>
              <h3 className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Pressure tests</h3>
              {cTests.length === 0 ? (
                <WlEmpty>No pressure test was recorded during this sheet.</WlEmpty>
              ) : (
                <ul className="space-y-1">
                  {cTests.map((c) => {
                    const t = readTest(c as any);
                    return row(c, t ? wlTestLine(t, tz) : c.id, pickedTests, setPickedTests);
                  })}
                </ul>
              )}
              <h3 className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Drills</h3>
              {cDrills.length === 0 ? (
                <WlEmpty>No drill was recorded during this sheet.</WlEmpty>
              ) : (
                <ul className="space-y-1">
                  {cDrills.map((c) => {
                    const d = readDrill(c as any);
                    return row(c, d ? wlDrillLine(d, tz) : c.id, pickedDrills, setPickedDrills);
                  })}
                </ul>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" className={WL_SECONDARY} onClick={() => setManaging(false)} disabled={setLinks.isPending}>
                  Cancel
                </button>
                <button type="button" className={WL_PRIMARY} onClick={save} disabled={setLinks.isPending}>
                  {setLinks.isPending ? 'Saving…' : 'Save links'}
                </button>
              </div>
            </>
          )
        ) : tests.length === 0 && drills.length === 0 ? (
          <WlEmpty>No pressure test or drill is linked to this sheet.</WlEmpty>
        ) : (
          <>
            {pendingCount > 0 && (
              <p className="text-sm text-fieldiq-muted">
                {pendingCount === 1 ? '1 record was' : `${pendingCount} records were`} recorded on this sheet by the crew and{' '}
                {pendingCount === 1 ? 'is' : 'are'} pending. Approving the sheet approves {pendingCount === 1 ? 'it' : 'them'}.
              </p>
            )}
            <ul className="space-y-1 text-sm text-fieldiq-text" data-testid="wl-linked-records">
              {tests.map((t) => (
                <li key={t.id} className="flex flex-wrap items-center gap-2" data-testid="wl-linked-test">
                  <span>Pressure test · {wlTestLine(t, tz)}</span>
                  <WlReviewBadge status={t.reviewStatus} />
                </li>
              ))}
              {drills.map((d) => (
                <li key={d.id} className="flex flex-wrap items-center gap-2" data-testid="wl-linked-drill">
                  <span>Drill · {wlDrillLine(d, tz)}</span>
                  <WlReviewBadge status={d.reviewStatus} />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </WlPanel>
  );
}
